<template>
  <div>
    <md-toolbar style="margin-bottom: 3rem" class="md-dense">
      <div class="md-toolbar-row">
        <div class="md-toolbar-section-start">
          <md-menu
            md-direction="bottom-end"
            md-size="big"
            :md-offset-x="127"
            :md-offset-y="-36"
          >
            <md-button md-menu-trigger>
              <md-icon>keyboard_arrow_down</md-icon>
              {{ $tc("words.cluster") }}:
              {{ $tc("phrases.allClusters") }}
            </md-button>
            <md-menu-content>
              <md-menu-item :disabled="true">
                <span>{{ $tc("phrases.allClusters") }}</span>
              </md-menu-item>
              <md-divider></md-divider>
              <md-menu-item
                v-for="(cluster, key) in clusterList"
                :key="key"
                @click="goToClusterDetail(cluster.id)"
              >
                <span>{{ cluster.name }}</span>
              </md-menu-item>
            </md-menu-content>
          </md-menu>
        </div>
        <div class="md-toolbar-section-end">
          <md-button class="md-raised" @click="updateCacheData">
            <md-icon>update</md-icon>
            {{ $tc("phrases.refreshData") }}
            <md-progress-bar
              v-if="loading"
              md-mode="indeterminate"
            ></md-progress-bar>
          </md-button>
        </div>
      </div>
    </md-toolbar>
    <div>
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-100">
          <box-group :clusters="clustersData" />
        </div>
        <div class="md-layout-item md-size-100">
          <financial-overview
            :revenue="clustersData"
            :periodChanged="financialOverviewPeriodChanged"
          />
        </div>
        <div class="md-layout-item md-size-100">
          <widget :title="$tc('phrases.clusterMap')" id="cluster-map">
            <dashboard-map
              :mapping-service="mappingService"
              ref="dashboardMapRef"
            />
          </widget>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"

import { geoJsonToLatLon } from "@/Helpers/Utils.js"
import { notify } from "@/mixins/notify.js"
import BoxGroup from "@/modules/Dashboard/BoxGroup.vue"
import FinancialOverview from "@/modules/Dashboard/FinancialOverview.vue"
import DashboardMap from "@/modules/Map/DashboardMap.vue"
import { MappingService, MARKER_TYPE } from "@/services/MappingService.js"
import "@/shared/TableList.vue"
import Widget from "@/shared/Widget.vue"

export default {
  name: "ClustersOverview",
  components: { DashboardMap, FinancialOverview, BoxGroup, Widget },
  mixins: [notify],
  data() {
    return {
      loading: false,
      clustersData: [],
      mappingService: new MappingService(),
    }
  },
  created() {
    this.getClusterList()
  },
  computed: {
    clusterList() {
      return this.$store.getters["clusterDashboard/getClustersData"].map(
        (cluster) => {
          return {
            id: cluster.id,
            name: cluster.clusterData?.name || cluster.name,
          }
        },
      )
    },
  },
  methods: {
    goToClusterDetail(clusterId) {
      this.$router.push("/clusters/" + clusterId)
    },
    async getClusterList() {
      this.loading = true
      await this.$store.dispatch("clusterDashboard/list")
      this.clustersData =
        this.$store.getters["clusterDashboard/getClustersData"]
      this.loading = false
      this.setClustersMapData()
    },
    async updateCacheData() {
      this.loading = true
      try {
        await this.$store.dispatch("clusterDashboard/update")
        this.clustersData =
          this.$store.getters["clusterDashboard/getClustersData"]
        this.alertNotify("success", "Dashboard data refreshed successfully.")
      } catch (e) {
        this.alertNotify("error", e.message)
      }
      this.loading = false
    },
    setClustersMapData() {
      const markingInfos = []
      const clustersGeoData = []
      this.clustersData.map((data) => {
        // Use geo_json from clusterData (the cluster model)
        const cluster = data.clusterData || data
        if (cluster.geo_json !== null && cluster.geo_json !== undefined) {
          // Overwrite properties.name with fresh cluster.name — the persisted
          // geo_json is a snapshot from creation and doesn't follow renames.
          let geoJsonFeature
          if (cluster.geo_json.type === "Feature") {
            geoJsonFeature = {
              ...cluster.geo_json,
              properties: {
                ...cluster.geo_json.properties,
                clusterId: data.id,
                name: cluster.name || "",
              },
            }
          } else if (cluster.geo_json.type === "FeatureCollection") {
            geoJsonFeature = {
              ...cluster.geo_json.features[0],
              properties: {
                ...cluster.geo_json.features[0].properties,
                clusterId: data.id,
                name: cluster.name || "",
              },
            }
          } else {
            throw new Error(
              "cluster.geo_json must be a GeoJSON Feature or FeatureCollection",
            )
          }

          clustersGeoData.push(geoJsonFeature)

          const miniGridsOfCluster = data.clusterData?.mini_grids || []
          miniGridsOfCluster.map((miniGrid) => {
            const location = geoJsonToLatLon(miniGrid.location)
            if (location == null) {
              this.alertNotify("error", "Site has no location")
              return
            }
            const lat = location.lat
            const lon = location.lon
            markingInfos.push({
              id: miniGrid.id,
              name: miniGrid.name,
              serialNumber: null,
              lat: lat,
              lon: lon,
              deviceType: null,
              markerType: MARKER_TYPE.MINI_GRID,
              clusterId: data.id,
              clusterName: cluster.name || "",
            })
          })
        }
      })
      this.mappingService.setGeoData(clustersGeoData)
      this.mappingService.setMarkingInfos(markingInfos)
      this.$refs.dashboardMapRef.drawClusters()
      this.$refs.dashboardMapRef.setMiniGridMarkers()
    },
    financialOverviewPeriodChanged(fromDate, toDate) {
      const cachedData = this.$store.getters["clusterDashboard/getClustersData"]
      this.clustersData = cachedData.map((cluster) => {
        const newPeriod = Object.entries(cluster.period).reduce(
          (acc, [period, revenue]) => {
            if (
              moment(period).isSameOrAfter(fromDate) &&
              moment(period).isSameOrBefore(toDate)
            ) {
              acc = { ...acc, [period]: revenue }
            }
            return acc
          },
          {},
        )
        return {
          ...cluster,
          period: newPeriod,
        }
      })
    },
  },
}
</script>
